import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ExternalLink,
  Flame,
  MessageCircle,
  FileText,
  Copy,
  Download,
  Bot,
  PenTool,
  Check,
  Link2,
  User,
  Loader2,
  Globe,
} from "lucide-react";
import api from "../api/client";

function ScoreBadge({ score }) {
  let color = "bg-gray-600 text-gray-300";
  if (score >= 60) color = "bg-red-500/20 text-red-400 border border-red-500/30";
  else if (score >= 40) color = "bg-amber-500/20 text-amber-400 border border-amber-500/30";
  else if (score >= 20) color = "bg-blue-500/20 text-blue-400 border border-blue-500/30";
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-bold ${color}`}>
      {score >= 60 && <Flame size={14} />}
      {score}
    </span>
  );
}

export default function SocialMonitorPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reply state
  const [replyTab, setReplyTab] = useState("ai");
  const [replyText, setReplyText] = useState("");
  const [includeBacklink, setIncludeBacklink] = useState(false);
  const [includeBio, setIncludeBio] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyCopied, setReplyCopied] = useState(false);

  // Blog state
  const [blogTab, setBlogTab] = useState("ai");
  const [blogTitle, setBlogTitle] = useState("");
  const [blogContent, setBlogContent] = useState("");
  const [blogMeta, setBlogMeta] = useState("");
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogCopied, setBlogCopied] = useState(false);

  useEffect(() => {
    api.get(`/social/posts/${id}/`).then((res) => {
      const p = res.data;
      setPost(p);
      if (p.reply_text) setReplyText(p.reply_text);
      if (p.blog_title) setBlogTitle(p.blog_title);
      if (p.blog_content) setBlogContent(p.blog_content);
      if (p.blog_meta_description) setBlogMeta(p.blog_meta_description);
      setLoading(false);
    });
  }, [id]);

  const handleAIReply = async () => {
    setReplyLoading(true);
    try {
      const res = await api.post(`/social/posts/${id}/reply/ai/`, {
        include_backlink: includeBacklink,
        include_bio: includeBio,
      });
      setPost(res.data);
      setReplyText(res.data.reply_text);
    } catch (err) {
      alert("Error generating reply");
    }
    setReplyLoading(false);
  };

  const handleManualReply = async () => {
    setReplyLoading(true);
    try {
      const res = await api.post(`/social/posts/${id}/reply/manual/`, {
        reply_text: replyText,
        include_backlink: includeBacklink,
        include_bio: includeBio,
      });
      setPost(res.data);
    } catch (err) {
      alert("Error saving reply");
    }
    setReplyLoading(false);
  };

  const handleAIBlog = async () => {
    setBlogLoading(true);
    try {
      const res = await api.post(`/social/posts/${id}/blog/ai/`);
      setPost(res.data);
      setBlogTitle(res.data.blog_title);
      setBlogContent(res.data.blog_content);
      setBlogMeta(res.data.blog_meta_description);
    } catch (err) {
      alert("Error generating blog");
    }
    setBlogLoading(false);
  };

  const handleManualBlog = async () => {
    setBlogLoading(true);
    try {
      const res = await api.post(`/social/posts/${id}/blog/manual/`, {
        title: blogTitle,
        content_markdown: blogContent,
        meta_description: blogMeta,
      });
      setPost(res.data);
    } catch (err) {
      alert("Error saving blog");
    }
    setBlogLoading(false);
  };

  const copyToClipboard = async (text, setCopied) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadBlog = () => {
    window.open(`${api.defaults.baseURL}/social/posts/${id}/download-blog/`, "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-copper" />
      </div>
    );
  }

  if (!post) return <div className="text-center py-20 text-gray-500">Post not found</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate("/social-monitor")}
        className="flex items-center gap-2 text-gray-400 hover:text-white text-sm transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Feed
      </button>

      {/* Source post */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-5">
        <div className="flex items-center gap-3 mb-3">
          <ScoreBadge score={post.score} />
          <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${post.platform === "quora" ? "bg-red-500/10 text-red-400" : "bg-blue-500/10 text-blue-400"}`}>
            {post.platform === "quora" ? "Quora" : `r/${post.subreddit}`}
          </span>
          <span className="text-[10px] text-gray-500">{post.business_display}</span>
          <a
            href={post.url}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-gray-500 hover:text-copper"
          >
            <ExternalLink size={14} />
          </a>
        </div>
        <h2 className="text-lg font-semibold text-white mb-2">{post.title}</h2>
        {post.body && (
          <div className="text-sm text-gray-400 whitespace-pre-wrap max-h-64 overflow-y-auto">
            {post.body}
          </div>
        )}
        <div className="flex items-center gap-4 mt-3 text-xs text-gray-600">
          <span>{post.platform === "quora" ? "Quora" : `by u/${post.author}`}</span>
          {post.platform !== "quora" && <span>{post.upvotes} upvotes</span>}
          {post.platform !== "quora" && <span>{post.num_comments} comments</span>}
          {post.keyword_matches?.length > 0 && (
            <div className="flex gap-1">
              {post.keyword_matches
                .filter((k) => k.category !== "negative")
                .map((k, i) => (
                  <span key={i} className="px-1.5 py-0.5 bg-copper/10 text-copper rounded text-[10px]">
                    {k.keyword}
                  </span>
                ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Reply section */}
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <MessageCircle size={16} className="text-green-400" />
              {post.platform === "quora" ? "Answer" : "Reply"}
            </h3>
            <div className="flex gap-1">
              {["ai", "manual"].map((t) => (
                <button
                  key={t}
                  onClick={() => setReplyTab(t)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                    replyTab === t
                      ? "bg-copper/20 text-copper"
                      : "text-gray-500 hover:text-white"
                  }`}
                >
                  {t === "ai" ? "AI Draft" : "Manual"}
                </button>
              ))}
            </div>
          </div>

          {/* Toggles */}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={includeBacklink}
                onChange={(e) => setIncludeBacklink(e.target.checked)}
                className="rounded border-dark-50"
              />
              <Link2 size={12} />
              Include Backlink
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={includeBio}
                onChange={(e) => setIncludeBio(e.target.checked)}
                className="rounded border-dark-50"
              />
              <User size={12} />
              Include Bio
            </label>
          </div>

          {replyTab === "ai" ? (
            <button
              onClick={handleAIReply}
              disabled={replyLoading}
              className="w-full px-4 py-2.5 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-xl text-sm font-semibold hover:bg-purple-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {replyLoading ? (
                <><Loader2 size={14} className="animate-spin" /> Generating...</>
              ) : (
                <><Bot size={14} /> {post.platform === "quora" ? "Generate AI Answer" : "Generate AI Reply"}</>
              )}
            </button>
          ) : null}

          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder={post.platform === "quora" ? "Write your answer..." : "Write your reply..."}
            rows={6}
            className="w-full px-4 py-3 bg-dark border border-dark-50 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper/50 resize-y"
          />

          <div className="flex gap-2">
            {replyTab === "manual" && (
              <button
                onClick={handleManualReply}
                disabled={replyLoading || !replyText.trim()}
                className="px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-semibold rounded-xl disabled:opacity-50"
              >
                {post.platform === "quora" ? "Save Answer" : "Save Reply"}
              </button>
            )}
            <button
              onClick={() => copyToClipboard(replyText, setReplyCopied)}
              disabled={!replyText}
              className="px-4 py-2 bg-dark-50 text-gray-400 hover:text-white rounded-xl text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {replyCopied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
              {replyCopied ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>

        {/* Blog section */}
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <FileText size={16} className="text-purple-400" />
              Blog Post
            </h3>
            <div className="flex gap-1">
              {["ai", "manual"].map((t) => (
                <button
                  key={t}
                  onClick={() => setBlogTab(t)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                    blogTab === t
                      ? "bg-copper/20 text-copper"
                      : "text-gray-500 hover:text-white"
                  }`}
                >
                  {t === "ai" ? "AI Draft" : "Manual"}
                </button>
              ))}
            </div>
          </div>

          {blogTab === "ai" ? (
            <button
              onClick={handleAIBlog}
              disabled={blogLoading}
              className="w-full px-4 py-2.5 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-xl text-sm font-semibold hover:bg-purple-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {blogLoading ? (
                <><Loader2 size={14} className="animate-spin" /> Generating...</>
              ) : (
                <><Bot size={14} /> Generate AI Blog</>
              )}
            </button>
          ) : null}

          <input
            type="text"
            value={blogTitle}
            onChange={(e) => setBlogTitle(e.target.value)}
            placeholder="Blog title..."
            className="w-full px-4 py-2.5 bg-dark border border-dark-50 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper/50"
          />
          <input
            type="text"
            value={blogMeta}
            onChange={(e) => setBlogMeta(e.target.value)}
            placeholder="Meta description (max 155 chars)..."
            maxLength={155}
            className="w-full px-4 py-2 bg-dark border border-dark-50 rounded-xl text-xs text-white placeholder-gray-600 focus:outline-none focus:border-copper/50"
          />
          <textarea
            value={blogContent}
            onChange={(e) => setBlogContent(e.target.value)}
            placeholder="Blog content in markdown..."
            rows={10}
            className="w-full px-4 py-3 bg-dark border border-dark-50 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none focus:border-copper/50 resize-y font-mono"
          />

          <div className="flex gap-2">
            {blogTab === "manual" && (
              <button
                onClick={handleManualBlog}
                disabled={blogLoading || !blogTitle.trim() || !blogContent.trim()}
                className="px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-semibold rounded-xl disabled:opacity-50"
              >
                Save Blog
              </button>
            )}
            <button
              onClick={() => copyToClipboard(blogContent, setBlogCopied)}
              disabled={!blogContent}
              className="px-4 py-2 bg-dark-50 text-gray-400 hover:text-white rounded-xl text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {blogCopied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
              {blogCopied ? "Copied!" : "Copy"}
            </button>
            {post.blog_html && (
              <button
                onClick={downloadBlog}
                className="px-4 py-2 bg-dark-50 text-gray-400 hover:text-white rounded-xl text-sm flex items-center gap-2"
              >
                <Download size={14} />
                Download HTML
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
